import { useMemo, useState, type FC } from "react";
import { useNavigate } from "react-router-dom";
import moment from "moment";

import { useAccessControl } from "../../../../app/access-control-context";
import { INQUIRY_EDIT } from "../../../../data/permissions";
import { useAppointmentFormModal } from "../../../../forms/appointment/appointment-form";
import { useCenterConstants } from "../../../../hooks/use-center";
import type { Inquiry, InquiryAppointment } from "../../../../hooks/use-inquiries";
import {
  useInquiry,
  useInquiryDashboard,
  useInquiryAppointments,
  useInquirySummary
} from "../../../../hooks/use-inquiries";
import { LoadingOverlay } from "../../../loading-overlay/loading-overlay";
import { useFamilyContactDetailFormModal } from "../edit-family-contact-form";
import { useStudentDetailFormModal } from "../edit-student-contact-form";
import { StudentDetailsTable } from "./student-detail-table";
import { UpcomingEventsTable } from "./upcoming-events-table";
import { FamilyContactsTable } from "./family-contacts-table";
import { AppointmentHistoryAccordion } from "./appointment-history-accordion";
import { useAppointmentSummaryModal } from "./appointment-summary";
import { LoadingButton } from "../../../loading-button/loading-button";
import { ServiceIdAccordion, type ServiceIdRow } from "./service-id-accordion";

export const InquiryDashboardInfo: FC<{ inquiry: Inquiry; onSelectServiceId?: (inquiryId: string) => void }> = ({
  inquiry,
  onSelectServiceId
}) => {
  const navigate = useNavigate();
  const [activeAppointment, setActiveAppointment] = useState<InquiryAppointment>();
  const [selectedUpcomingAppointment, setSelectedUpcomingAppointment] = useState<InquiryAppointment>();
  const { activeCenterId, hasPermission } = useAccessControl();
  const dashboardQuery = useInquiryDashboard({
    centerId: activeCenterId,
    inquiryId: inquiry.id
  });
  const inquiryDetailQuery = useInquiry(inquiry.id);
  const inquirySummaryQuery = useInquirySummary(inquiry.id);
  const {
    activeConstants: { services, programs, subjects, tests }
  } = useCenterConstants(activeCenterId);
  const { createEventMutator, updateEventMutator } = useInquiryAppointments({
    centerId: activeCenterId,
    inquiryId: inquiry.id
  });

  const { showFamilyContactFormModal, renderFamilyContactFormModal } = useFamilyContactDetailFormModal();

  const handleSelectContact = (contactId: number) => {
    showFamilyContactFormModal({ contactId, inquiryId: inquiry.id });
  };

  const handleUpdateAppointmentStatus = (appointment: InquiryAppointment) => {
    updateEventMutator.mutate(
      {
        ...appointment,
        scheduledDateTime: moment.utc(appointment.scheduledDateTime).format("YYYY-MM-DDTHH:mm:ssZ"),
        ...(appointment.endDateTime && {
          endDateTime: moment.utc(appointment.endDateTime).format("YYYY-MM-DDTHH:mm:ssZ")
        }),
        staffMembers: appointment.staffMembers.map(({ id }) => id).filter(id => id),
        attendees: appointment.attendees.map(({ id }) => id)
      },
      {
        onSuccess: () => {
          navigate(`/inquiry/${inquiry.id}`);
        }
      }
    );
  };

  const handleRescheduleAppointment = (appointment: InquiryAppointment) => {
    setActiveAppointment(appointment);
    showAppointmentFormModal();
  };

  const handleCreateNewAppointment = () => {
    setActiveAppointment(undefined);
    showAppointmentFormModal();
  };

  const handleClickUpcomingAppointment = (appointment: InquiryAppointment) => {
    setSelectedUpcomingAppointment(appointment);
    showAppointmentSummaryModal();
  };

  const { showAppointmentFormModal, renderAppointmentFormModal } = useAppointmentFormModal({
    inquiryId: inquiry.id,
    appointment: activeAppointment,
    centerId: activeCenterId
  });

  const { showStudentDetailFormModal, renderStudentDetailFormModal } = useStudentDetailFormModal();

  const { showAppointmentSummaryModal, renderAppointmentSummaryModal } = useAppointmentSummaryModal({
    student: dashboardQuery.data?.student,
    familyInfo: dashboardQuery.data?.familyInfo,
    contacts: dashboardQuery.data?.attendees,
    appointment: selectedUpcomingAppointment,
    onUpdateAppointmentStatus: handleUpdateAppointmentStatus,
    onRescheduleAppointment: handleRescheduleAppointment
  });

  // need to resolve IDs to labels using lookup dictionaries and normalize multiple possible source fields
  const serviceIdRows = useMemo<ServiceIdRow[]>(() => {
    const serviceId = inquiryDetailQuery.data?.legacyClientId;

    const serviceName =
      findReferenceName(services, inquiryDetailQuery.data?.serviceId) ||
      dashboardQuery.data?.inquiry?.[0]?.service ||
      "";
    const subserviceName = findReferenceName(programs, inquiryDetailQuery.data?.programId);
    const subjectOrTestName = getSubjectOrTestName({
      subjectId: inquiryDetailQuery.data?.subjectId,
      testId: inquiryDetailQuery.data?.testId,
      selectedSubjectOrTest: inquiryDetailQuery.data?.selectedSubjectOrTest,
      multipleSubjectIds: inquiryDetailQuery.data?.multipleSubjectIds,
      subjects,
      tests
    });

    return [
      {
        inquiryId: inquiry.id,
        serviceId: serviceId || "-",
        service: serviceName || "-",
        subservice: subserviceName || "-",
        subjectOrTest: subjectOrTestName || "-"
      }
    ];
  }, [
    inquiry.id,
    inquiryDetailQuery.data?.legacyClientId,
    inquiryDetailQuery.data?.serviceId,
    inquiryDetailQuery.data?.programId,
    inquiryDetailQuery.data?.subjectId,
    inquiryDetailQuery.data?.testId,
    inquiryDetailQuery.data?.selectedSubjectOrTest,
    inquiryDetailQuery.data?.multipleSubjectIds,
    services,
    programs,
    subjects,
    tests,
    dashboardQuery.data?.inquiry
  ]);

  const isAppointmentActionProcessing = inquirySummaryQuery.data?.isProcessingStatus ?? inquiry.isProcessingStatus;

  return (
    <LoadingOverlay loading={dashboardQuery.isLoading || createEventMutator.isLoading || updateEventMutator.isLoading}>
      <div className="card-body">
        <StudentDetailsTable
          inquiryDashboardInfo={dashboardQuery.data?.inquiry || []}
          onClickStudent={showStudentDetailFormModal}
        />
        {renderStudentDetailFormModal()}

        <ServiceIdAccordion rows={serviceIdRows} onSelectServiceId={onSelectServiceId} />

        <div className="d-flex mt-4 mb-4 align-items-center justify-content-between">
          <h3 className="card-subheader mb-0">Primary/Emergency Contacts</h3>
        </div>

        <FamilyContactsTable contacts={dashboardQuery.data?.contacts} onSelectContact={handleSelectContact} />

        <div className="d-flex mt-4 mb-4 align-items-center justify-content-between">
          <h3 className="card-subheader mb-0">Upcoming Appointments</h3>
          {hasPermission && hasPermission(INQUIRY_EDIT) && (
            <LoadingButton
              className="btn btn-primary btn-sm ms-2"
              onClick={handleCreateNewAppointment}
              isLoading={isAppointmentActionProcessing}
              loadingMessage="Processing..."
            >
              Create Appointment
            </LoadingButton>
          )}
        </div>

        <UpcomingEventsTable
          upcomingEvents={dashboardQuery.data?.upcomingEvents}
          onUpdateAppointmentStatus={handleUpdateAppointmentStatus}
          onRescheduleAppointment={handleRescheduleAppointment}
          onClickUpcomingEvent={handleClickUpcomingAppointment}
        />

        {renderAppointmentSummaryModal()}

        {renderAppointmentFormModal()}
        {renderFamilyContactFormModal()}
      </div>
      <AppointmentHistoryAccordion className="mt-4" appointmentHistory={dashboardQuery.data?.pastEvents} />
    </LoadingOverlay>
  );
};

// following existing patterns:
// service/program/subject/test names are resolved from center constants,
// and subject/test can arrive as selectedSubjectOrTest, subjectId/testId, or multipleSubjectIds.
const findReferenceName = (items: { value: string | number; name: string }[], value?: string | number) =>
  items.find(item => String(item.value) === String(value))?.name ?? "";

const getSubjectOrTestName = ({
  subjectId,
  testId,
  selectedSubjectOrTest,
  multipleSubjectIds,
  subjects,
  tests
}: {
  subjectId?: string;
  testId?: string;
  selectedSubjectOrTest?: string;
  multipleSubjectIds?: string[];
  subjects: { value: string | number; name: string }[];
  tests: { value: string | number; name: string }[];
}) => {
  if (multipleSubjectIds?.length) {
    return multipleSubjectIds
      .map(id => findReferenceName(subjects, id))
      .filter(Boolean)
      .join(", ");
  }

  const [selectedId, selectedType] = selectedSubjectOrTest?.split("::") ?? [];
  if (selectedType === "subjectId") {
    return findReferenceName(subjects, selectedId);
  }

  if (selectedType === "testId") {
    return findReferenceName(tests, selectedId);
  }

  if (subjectId) {
    return findReferenceName(subjects, subjectId);
  }

  if (testId) {
    return findReferenceName(tests, testId);
  }

  return "";
};
